import React, { useState, useEffect } from 'react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useBusinessSettings } from '../../../hooks/useBusinessSettings';
import { getUsers } from '../../../services/api/users';
import styles from './BusinessFeatureSettings.module.css';

const BusinessFeatureSettings = () => {
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const messageCenter = useBusinessSettings('messageCenter');
  const crossSelling = useBusinessSettings('crossSelling');
  const orderDispatch = useBusinessSettings('orderDispatch');
  const defaultSystemUser = useBusinessSettings('DefaultSystemUser');

  const [activeUsers, setActiveUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const res = await getUsers({ status: 'true', limit: 1000 });
        setActiveUsers(res.users || res || []);
      } catch (err) {
        console.error('Failed to load users:', err);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, []);
  const orderAutoCancel = useBusinessSettings('orderAutoCancel');

  const [dayCountInput, setDayCountInput] = useState('');

  useEffect(() => {
    setDayCountInput(orderAutoCancel.settings.dayCount ?? '');
  }, [orderAutoCancel.settings.dayCount]);

  const handleToggle = async (settingsHook, checked, key = 'enabled') => {
    try {
      await settingsHook.saveSettings({ ...settingsHook.settings, [key]: checked });
    } catch (err) {
      console.error('Failed to save business setting:', err);
      showError(t('business.featureSettingSaveError') || 'Failed to save setting');
    }
  };

  const handleDefaultSystemUserChange = async (e) => {
    const value = e.target.value;
    try {
      await defaultSystemUser.saveSettings({
        ...defaultSystemUser.settings,
        userId: value ? Number(value) : null,
      });
    } catch (err) {
      console.error('Failed to save business setting:', err);
      showError(t('business.featureSettingSaveError') || 'Failed to save setting');
    }
  };

  const handleDayCountSave = async () => {
    const parsed = dayCountInput === '' ? null : parseInt(dayCountInput, 10);
    if (dayCountInput !== '' && (!Number.isInteger(parsed) || parsed <= 0)) {
      showError(t('business.orderAutoCancelInvalid') || 'Day count must be a positive whole number');
      setDayCountInput(orderAutoCancel.settings.dayCount ?? '');
      return;
    }

    try {
      await orderAutoCancel.saveSettings({ ...orderAutoCancel.settings, dayCount: parsed });
    } catch (err) {
      console.error('Failed to save business setting:', err);
      showError(t('business.featureSettingSaveError') || 'Failed to save setting');
    }
  };

  return (
    <div className={styles.section}>
      <h3 className={styles.title}>{t('business.featureSettings') || 'Feature Settings'}</h3>

      <div className={styles.toggleRow}>
        <div>
          <div className={styles.fieldLabel}>{t('business.messageCenterShifts') || 'Message center shifts'}</div>
          <div className={styles.fieldHelp}>
            {t('business.messageCenterShiftsHelp') || 'Allow selecting a shift when creating/editing orders'}
          </div>
        </div>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={!!messageCenter.settings.enabled}
            disabled={messageCenter.isLoading}
            onChange={(e) => handleToggle(messageCenter, e.target.checked)}
          />
          <span className={styles.toggleSlider} />
        </label>
      </div>

      <div className={styles.toggleRow}>
        <div>
          <div className={styles.fieldLabel}>{t('business.crossSellingCommission') || 'Cross selling commission'}</div>
          <div className={styles.fieldHelp}>
            {t('business.crossSellingCommissionHelp') || 'Allow marking order items as cross-sold for commission tracking'}
          </div>
        </div>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={!!crossSelling.settings.enabled}
            disabled={crossSelling.isLoading}
            onChange={(e) => handleToggle(crossSelling, e.target.checked)}
          />
          <span className={styles.toggleSlider} />
        </label>
      </div>

      <div className={styles.toggleRow}>
        <div>
          <div className={styles.fieldLabel}>{t('business.stopUnverifiedDispatch') || 'Stop unverified order dispatching'}</div>
          <div className={styles.fieldHelp}>
            {t('business.stopUnverifiedDispatchHelp') || 'Exclude orders with an unverified payment record from packing batch creation'}
          </div>
        </div>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={!!orderDispatch.settings.stopUnverifiedDispatch}
            disabled={orderDispatch.isLoading}
            onChange={(e) => handleToggle(orderDispatch, e.target.checked, 'stopUnverifiedDispatch')}
          />
          <span className={styles.toggleSlider} />
        </label>
      </div>

      <div className={styles.toggleRow}>
        <div>
          <div className={styles.fieldLabel}>{t('business.orderAutoCancel') || 'Set order auto cancelled'}</div>
          <div className={styles.fieldHelp}>
            {t('business.orderAutoCancelHelp') || 'Automatically cancel pending orders with no verified payment after this many days. Leave empty to disable.'}
          </div>
        </div>
        <input
          type="number"
          min="1"
          step="1"
          className={styles.dayCountInput}
          value={dayCountInput}
          disabled={orderAutoCancel.isLoading}
          placeholder={t('business.orderAutoCancelPlaceholder') || 'Days'}
          onChange={(e) => setDayCountInput(e.target.value)}
          onBlur={handleDayCountSave}
        />
      </div>

      <div className={styles.toggleRow}>
        <div>
          <div className={styles.fieldLabel}>{t('business.defaultSystemUser') || 'Default System User'}</div>
          <div className={styles.fieldHelp}>
            {t('business.defaultSystemUserHelp') || 'Select a user to act as the system user for this business'}
          </div>
        </div>
        <select
          className={styles.select}
          value={defaultSystemUser.settings.userId || ''}
          disabled={defaultSystemUser.isLoading || usersLoading}
          onChange={handleDefaultSystemUserChange}
        >
          <option value="">{t('business.noUserSelected') || '— None selected —'}</option>
          {activeUsers.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name} ({user.email})
            </option>
          ))}
        </select>
      </div>

    </div>
  );
};

export default BusinessFeatureSettings;
